import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router'; // Import Router, ActivatedRoute
import { OrdenesTransporteService } from '../../../../services/ordenes-transporte.service';
import { TransportOrdersNavigationService } from '../../../../services/transport-orders-navigation.service';
import {
  OrderDetailDto as TransportOrder,
  TransportOrderDetailResponseDto as TransportOrderDetailResponse,
  TransportOrderItemDto as TransportOrderItem,
  ServiceOrderDto as ServiceOrder,
} from '@opb-workspace/shared';

@Component({
  selector: 'app-transport-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    FormsModule
  ],
  templateUrl: './transport-order-detail.component.html',
  styleUrls: ['./transport-order-detail.component.scss'],
})
export class TransportOrderDetailComponent implements OnInit {
  orderDetail: TransportOrderDetailResponse | null = null;
  loading = true;
  error: string | null = null;
  selectedItemId: number | 'all' = 'all';

  constructor(
    private ordenesTransporteService: OrdenesTransporteService,
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: TransportOrdersNavigationService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.fetchOrderDetail(Number(id));
    } else {
      this.error = 'No ID provided for Transport Order';
      this.loading = false;
    }
  }

  goBack(): void {
    this.navigationService.backToList();
  }

  get filteredItems(): TransportOrderItem[] {
    if (!this.orderDetail?.items) return [];
    if (this.selectedItemId === 'all') return this.orderDetail.items;
    return this.orderDetail.items.filter(item => item.Id === this.selectedItemId);
  }

  get filteredServiceOrders(): ServiceOrder[] {
    if (!this.orderDetail?.serviceOrders) return [];
    if (this.selectedItemId === 'all') return this.orderDetail.serviceOrders;
    return this.orderDetail.serviceOrders.filter(so => so.TransportOrderItemId === this.selectedItemId);
  }

  fetchOrderDetail(id: number): void {
    this.loading = true;
    this.ordenesTransporteService.getTransportOrderDetail(id).subscribe({
      next: (data) => {
        this.orderDetail = data;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching order detail', err);
        this.error = 'Error loading order details. Please try again later.';
        this.loading = false;
      },
    });
  }

  get containers(): TransportOrderItem[] {
    return this.orderDetail?.items?.filter(i => i.TypeOfShipmentName === 'Contenedor') || [];
  }

  get filteredContainers(): TransportOrderItem[] {
    return this.filteredItems.filter(i => i.TypeOfShipmentName === 'Contenedor');
  }

  get pallets(): TransportOrderItem[] {
    return this.orderDetail?.items?.filter(i => i.TypeOfShipmentName === 'Pallet') || [];
  }

  get filteredPallets(): TransportOrderItem[] {
    return this.filteredItems.filter(i => i.TypeOfShipmentName === 'Pallet');
  }

  get serviceOrders(): ServiceOrder[] {
    return this.orderDetail?.serviceOrders || [];
  }

  get isContainer(): boolean {
    return this.orderDetail?.order?.TypeOfShipmentName === 'Contenedor';
  }

  get isPallet(): boolean {
    return this.orderDetail?.order?.TypeOfShipmentName === 'Pallet';
  }

  getStatusClass(status: string | null | undefined): string {
    if (!status) return 'status-default';
    switch (status.toLowerCase()) {
      case 'liquidado': return 'status-liquidado';
      case 'cerrado': return 'status-cerrado';
      case 'activo': return 'status-activo';
      default: return 'status-default';
    }
  }

  // Helper for generating leading zeros
  padNumber(num: number): string {
    return String(num).padStart(8, '0');
  }

  // Validates if date string is usable
  isValidDate(dateStr: string | null | undefined): boolean {
    return !!dateStr && dateStr !== 'NULL' && dateStr !== '';
  }
  getTimelineEvents(item: TransportOrderItem): any[] {
    // Agenda Logic
    const events: any[] = [];

    const type = item.TypeOfShipmentName ? item.TypeOfShipmentName.toLowerCase() : '';
    const isContainer = type.includes('contenedor');
    const isPallet = type.includes('pallet');
    const isLand = type.includes('terrestre'); // Strict check, no fallback

    // Helper to add event if date exists
    const addEvent = (title: string, date: string | null | undefined, desc: string | null = '', loc: string | null = '') => {
      if (this.isValidDate(date)) {
        events.push({
          title,
          date,
          description: desc || '',
          assignedTo: loc || ''
        });
      }
    };

    if (isContainer) {
      // 1. Llegada Depósito
      addEvent('Llegada Depósito', item.GateInDepotDate, '', item.DepotName);
      // 2. Salida Depósito
      addEvent('Salida Depósito', item.GateOutDepotDate, item.RemarksGateOutDepot || 'Retiro unidad vacía', item.DepotName);
      // 3. Ingreso Planta
      addEvent('Ingreso Planta', item.GateInDate, '', item.FirstDispatchPlantName);
      // 4. Salida Planta
      addEvent('Salida Planta', item.GateOutDate, '', item.FirstDispatchPlantName);
      // 5. Visación
      addEvent('Visación', item.SAGCustomsInspectionDate, '', '');
      // 6. Ingreso Antepuerto
      addEvent('Ingreso Antepuerto', item.GateInTerminalDate, '', this.orderDetail?.order?.PortOfLoadingName);
      // 7. Entregado Stacking
      addEvent('Entregado Stacking', item.GateInStackingDate, '', '');
      // 8. Salida Puerto
      addEvent('Salida Puerto', item.GateOutTerminalDate, '', '');

    } else if (isPallet) {
      // 1. Ingreso Planta
      addEvent('Ingreso Planta', item.GateInDate, '', item.FirstDispatchPlantName);
      // 2. Salida Planta
      addEvent('Salida Planta', item.GateOutDate, '', item.FirstDispatchPlantName);
      // 3. Entrada Puerto
      addEvent('Entrada Puerto', item.GateInTerminalDate, '', this.orderDetail?.order?.PortOfLoadingName);
      // 4. Entrada Stacking
      addEvent('Entrada Stacking', item.GateInStackingDate, '', '');
      // Include Citación Planta as per request "Incluir Citación Planta"
      addEvent('Citación Planta', item.FirstDispatchPlantCitationDate, `Planta: ${item.FirstDispatchPlantName}`, item.FirstDispatchPlantLocationName);

    }
    // No else block - if not Container or Pallet, show nothing.

    // Duración entre cada par de hitos consecutivos que tengan fecha (CA-08/CA-09) —
    // no solo los 3 tramos con nombre propio (Depósito/Planta/Terminal), sino
    // cualquier par adyacente en la lista ya filtrada por isValidDate().
    this.attachDurations(events);

    return events;
  }

  hasAgendaEvents(): boolean {
    return this.orderDetail?.items?.some(item => this.getTimelineEvents(item).length > 0) ?? false;
  }

  // Clases de Tabler Icons (@tabler/icons-webfont, registrado en angular.json)
  private static readonly EVENT_ICONS: Record<string, string> = {
    'Llegada Depósito': 'ti ti-building-warehouse',
    'Salida Depósito': 'ti ti-building-warehouse',
    'Ingreso Planta': 'ti ti-building-factory',
    'Salida Planta': 'ti ti-building-factory',
    'Visación': 'ti ti-file-check',
    'Ingreso Antepuerto': 'ti ti-torii',
    'Entregado Stacking': 'ti ti-stack',
    'Salida Puerto': 'ti ti-ship',
    'Entrada Puerto': 'ti ti-torii',
    'Entrada Stacking': 'ti ti-stack',
    'Citación Planta': 'ti ti-calendar-event',
  };

  // Ícono por tipo de hito, según el mockup de Agenda
  getEventIcon(title: string): string {
    return TransportOrderDetailComponent.EVENT_ICONS[title] || 'ti ti-calendar';
  }

  // Parsea una fecha formateada por el backend como "DD/MM/YYYY HH:mm"
  private parseFormattedDate(value: string | null | undefined): Date | null {
    if (!this.isValidDate(value)) return null;
    const [datePart, timePart] = (value as string).split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes);
    return isNaN(date.getTime()) ? null : date;
  }

  // Duración entre dos fechas formateadas, en formato "00h 20m" (CA-08)
  getDurationBetween(start: string | null | undefined, end: string | null | undefined): string | null {
    const startDate = this.parseFormattedDate(start);
    const endDate = this.parseFormattedDate(end);
    if (!startDate || !endDate) return null;
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return null;
    const totalMinutes = Math.round(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
  }

  // Anota la duración entre cada hito y el inmediatamente anterior de la lista (CA-08/CA-09)
  private attachDurations(events: any[]): void {
    for (let i = 1; i < events.length; i++) {
      const duration = this.getDurationBetween(events[i - 1].date, events[i].date);
      if (duration) {
        events[i].duration = duration;
      }
    }
  }

  /**
   * Muestra el total calculado en backend.
   * Si las monedas son diferentes, muestra ambos valores separados.
   */
  getTotalDisplay(): string {
    if (!this.orderDetail?.order) return '-';

    const order = this.orderDetail.order;

    // Usar TotalValue calculado en backend si está disponible
    if (order.TotalValue) {
      return this.removeClpSuffix(order.TotalValue);
    }

    // Si hay monedas mixtas (TotalValue es null), mostrar ambos valores
    if (order.HasMixedCurrencies) {
      return `${this.removeClpSuffix(order.NetValue)} + ${this.removeClpSuffix(order.ExtraCosts)}`;
    }

    return this.removeClpSuffix(order.NetValue) || '0';
  }

  /**
   * Remueve el sufijo "CLP" de un valor formateado si existe
   * Para usar en las columnas del detalle donde no debe aparecer el sufijo
   */
  removeClpSuffix(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }
    
    // Convertir a string si es número
    const stringValue = typeof value === 'number' ? value.toString() : value;
    
    if (typeof stringValue !== 'string' || stringValue.trim() === '') {
      return '—';
    }
    
    // Remover "CLP" del final del string (con espacios antes si existen)
    return stringValue.replace(/\s*CLP\s*$/i, '').trim();
  }
}
